import { useId } from "react";
import { Dialog, Modal, ModalOverlay } from "@its-engineering-and-development/ui-foundation/components";
import { buildCannotApproveParagraphs, CANNOT_APPROVE_COPY } from "@app/utils/policy-violations";
import type { ApprovalBlockingKinds, ViolationPopupTone } from "@app/utils/policy-violations";

const MODAL_OVERLAY_CLASS = "fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4";

const POLICY_VIOLATION_MODAL_THEME = {
    required: {
        title: "Explanation Required",
        accent: "text-red-600",
        iconCircle: "bg-red-100 text-red-600",
        card: "border-red-200 bg-red-50",
        cardTitle: "text-red-700",
    },
    warning: {
        title: "Policy Violation",
        accent: "text-orange-500",
        iconCircle: "bg-orange-100 text-orange-500",
        card: "border-orange-200 bg-orange-50",
        cardTitle: "text-orange-900",
    },
} as const satisfies Record<ViolationPopupTone, { title: string; accent: string; iconCircle: string; card: string; cardTitle: string }>;

export interface PolicyViolationModalProps {
    tone: ViolationPopupTone;
    policyName?: string | null;
    merchant: string;
    amount: string;
    date: string;
    onDismiss: () => void;
}

/** Informational popup shown after an approver saves an expense that has an active policy violation. */
export function PolicyViolationModal({ tone, policyName, merchant, amount, date, onDismiss }: PolicyViolationModalProps) {
    const titleId = useId();
    const theme = POLICY_VIOLATION_MODAL_THEME[tone];

    return (
        <ModalOverlay
            isOpen
            isDismissable
            onOpenChange={(open) => {
                if (!open) onDismiss();
            }}
            className={MODAL_OVERLAY_CLASS}
        >
            <Modal className="w-full max-w-sm">
                <Dialog aria-labelledby={titleId} className="outline-none">
                    <div className="relative w-full rounded-xl bg-white p-5 shadow-xl">
                        <button
                            type="button"
                            aria-label="Close"
                            className="absolute right-4 top-4 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            onClick={onDismiss}
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                                <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <div className={`mx-auto flex h-11 w-11 items-center justify-center rounded-full ${theme.iconCircle}`}>
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                                <circle cx="12" cy="12" r="9" />
                                <path strokeLinecap="round" d="M12 7.5v5" />
                                <path strokeLinecap="round" d="M12 16h.01" />
                            </svg>
                        </div>
                        <h2 id={titleId} className={`mt-3 text-center text-lg font-medium ${theme.accent}`}>
                            {theme.title}
                        </h2>
                        <div className={`mt-3 rounded-lg border px-4 py-3 ${theme.card}`}>
                            <p className={`text-base font-semibold ${theme.cardTitle}`}>{policyName || "Policy Violation"}</p>
                            <p className="mt-0.5 text-xs text-gray-600">{[merchant, amount, date].join(" · ")}</p>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <button
                                type="button"
                                className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-700"
                                onClick={onDismiss}
                                autoFocus
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}

export interface CannotApproveReportModalProps {
    kinds: ApprovalBlockingKinds;
    isProcessing: boolean;
    onSendBack: () => void;
    onCancel: () => void;
}

/** Same layout as the "Approve Report" confirmation, but the only forward action is "Send Back". */
export function CannotApproveReportModal({ kinds, isProcessing, onSendBack, onCancel }: CannotApproveReportModalProps) {
    const titleId = useId();

    return (
        <ModalOverlay
            isOpen
            isDismissable={!isProcessing}
            isKeyboardDismissDisabled={isProcessing}
            onOpenChange={(open) => {
                if (!open && !isProcessing) onCancel();
            }}
            className={MODAL_OVERLAY_CLASS}
        >
            <Modal className="w-full max-w-md">
                <Dialog aria-labelledby={titleId} className="outline-none">
                    <div className="w-full rounded-xl bg-white p-6 shadow-xl">
                        <h2 id={titleId} className="text-lg font-semibold text-gray-900">
                            {CANNOT_APPROVE_COPY.title}
                        </h2>
                        <div className="mt-2 space-y-2 text-sm text-gray-600">
                            {buildCannotApproveParagraphs(kinds).map((paragraph) => (
                                <p key={paragraph}>{paragraph}</p>
                            ))}
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-xs hover:bg-gray-50"
                                onClick={onCancel}
                                disabled={isProcessing}
                                autoFocus
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-xs hover:bg-red-700 disabled:opacity-50"
                                onClick={onSendBack}
                                disabled={isProcessing}
                            >
                                {isProcessing ? "Sending…" : "Send Back"}
                            </button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}
